import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClockIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { whenLabel, type ScheduleRecord } from '@/pages/assistant/shared';

/** The schedule fields the search box matches. */
const SCHEDULE_SEARCH_FIELDS: ReadonlyArray<keyof ScheduleRecord> = ['name', 'skillName', 'cron', 'timezone'];

/**
 * Scheduled skill runs — the skill defines the process, the schedule the
 * time, parameters and delivery. Results land in the owner's Chats. Same
 * list grammar as the other assistant pages.
 */
export function SchedulesPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState<ScheduleRecord | null>(null);
  const { search, setSearch } = useUrlSearch();

  const schedules = useQuery({
    queryKey: ['assistant-schedules'],
    queryFn: ({ signal }) => getJson<ScheduleRecord[]>('/assistant/schedules', undefined, signal),
  });

  const rows = filterRows(schedules.data ?? [], search, SCHEDULE_SEARCH_FIELDS);

  const remove = useMutation({
    mutationFn: (id: string) => sendJson('delete', `/assistant/schedules/${id}`),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['assistant-schedules'] });
    },
  });

  return (
    <TooltipProvider delayDuration={100}>
      <section className="animate-portal-rise">
        <div className="flex items-start justify-between gap-4">
          <PageHeading
            eyebrow="AI ASSISTANT"
            title="Schedules"
            subtitle="Skills that run on their own — reports arrive in your Chats."
          />
          <Button onClick={() => navigate('/assistant/schedules/new')}>
            <PlusIcon aria-hidden className="size-4" /> New schedule
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search schedules"
            placeholder="Name, skill or cadence"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>

        {schedules.isPending ? (
          <SkeletonTable label="Loading schedules…" />
        ) : schedules.isError ? (
          <ErrorNotice title="Schedules unavailable" message={errorMessage(schedules.error)} />
        ) : schedules.data.length === 0 ? (
          <Panel>
            <EmptyState
              icon={<AlarmClockIcon aria-hidden className="size-6" />}
              title="Nothing scheduled yet"
              message='Schedule a skill here, or tell the assistant — "run the shipment check every morning at 4".'
            />
          </Panel>
        ) : rows.length === 0 ? (
          <Panel>
            <EmptyState title="No schedules match" message="Try a different search." />
          </Panel>
        ) : (
          <DataTable headers={['Schedule', 'Cadence', 'Next run', 'Last run', '']} minWidth={760}>
            {rows.map((schedule) => (
              <tr key={schedule.id} className="border-t border-portal-line align-top">
                <td className="p-3">
                  <Link
                    to={`/assistant/schedules/${schedule.id}`}
                    className="text-portal-small font-bold text-brand hover:underline"
                  >
                    {schedule.name}
                  </Link>
                  <span className="block font-mono text-portal-micro text-portal-muted">
                    {schedule.skillName}
                  </span>
                </td>
                <td className="p-3 text-portal-small">
                  <span className="font-mono">{schedule.cron}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {schedule.timezone}
                  </span>
                </td>
                <td className="p-3 text-portal-small text-portal-muted">
                  {schedule.enabled ? (whenLabel(schedule.nextRunAt) ?? '—') : <Pill>Disabled</Pill>}
                </td>
                <td className="p-3">
                  {schedule.lastStatus === null ? (
                    <span className="text-portal-small text-portal-muted">—</span>
                  ) : (
                    <Pill
                      tone={
                        schedule.lastStatus === 'ok'
                          ? 'success'
                          : schedule.lastStatus === 'error'
                            ? 'danger'
                            : 'neutral'
                      }
                    >
                      {schedule.lastStatus}
                    </Pill>
                  )}
                </td>
                <td className="p-3">
                  <span className="flex items-center justify-end gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to={`/assistant/schedules/${schedule.id}/edit`}
                          aria-label="Edit schedule"
                          className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                        >
                          <PencilIcon aria-hidden className="size-3.5" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Edit</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Delete schedule"
                          onClick={() => setDeleting(schedule)}
                          className="grid size-6 cursor-pointer place-items-center rounded-md text-portal-muted hover:bg-red-50 hover:text-red-600"
                        >
                          <XIcon aria-hidden className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">Delete</TooltipContent>
                    </Tooltip>
                  </span>
                </td>
              </tr>
            ))}
          </DataTable>
        )}

        <ConfirmDialog
          open={deleting !== null}
          tone="danger"
          title="Delete this schedule?"
          message={deleting ? `"${deleting.name}" will stop running.` : ''}
          confirmLabel="Delete schedule"
          onConfirm={() => {
            if (deleting) remove.mutate(deleting.id);
          }}
          onCancel={() => {
            if (!remove.isPending) setDeleting(null);
          }}
          pending={remove.isPending}
        />
      </section>
    </TooltipProvider>
  );
}
